import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Shield, Smartphone, Copy, Check, MessageSquare, AlertTriangle } from "lucide-react";
import { formatPhoneNumber } from "@/lib/utils";
import { getToken, apiUrl, credentialsMode } from "@/lib/net";

const setupSchema = z.object({
  phoneNumber: z.string().min(10, "Please enter a valid phone number")
});

interface MFASetupProps {
  onComplete: () => void;
  userRole: string;
  tempToken?: string;
  /** Changing an EXISTING MFA phone also needs the current password (audit M3). */
  requirePassword?: boolean;
}

export function MFASetup({ onComplete, userRole, tempToken, requirePassword }: MFASetupProps) {
  const [step, setStep] = useState<'phone' | 'code' | 'backup'>('phone');
  const [code, setCode] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const { toast } = useToast();

  const form = useForm<z.infer<typeof setupSchema>>({
    resolver: zodResolver(setupSchema),
    defaultValues: {
      phoneNumber: ""
    }
  });

  // Audit 2026-09-27 M3: two steps — the new number must receive and return a code
  // before it becomes the second factor.
  const postEnable = async (body: Record<string, unknown>) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const bearer = tempToken || getToken();
    if (bearer) headers['Authorization'] = `Bearer ${bearer}`;
    return fetch(apiUrl('/api/auth/mfa/enable'), {
      method: 'POST',
      headers,
      credentials: credentialsMode(),
      body: JSON.stringify(body),
    });
  };

  const fail = async (response: Response, fallback: string) => {
    let message = fallback;
    try { message = (await response.json())?.message || fallback; } catch { /* non-JSON */ }
    toast({ title: "Setup Failed", description: message, variant: "destructive" });
  };

  const enableMFA = async (data: z.infer<typeof setupSchema>) => {
    setIsLoading(true);
    try {
      const response = await postEnable({
        phoneNumber: data.phoneNumber,
        ...(requirePassword ? { currentPassword } : {}),
      });
      if (response.status === 202) {
        setCode("");
        setStep('code');
        toast({ title: "Code sent", description: "Enter the 6-digit code we sent to that WhatsApp number." });
      } else {
        await fail(response, "Failed to send the verification code");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to enable MFA. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const confirmCode = async () => {
    setIsLoading(true);
    try {
      const response = await postEnable({
        phoneNumber: form.getValues('phoneNumber'),
        code: code.trim(),
        ...(requirePassword ? { currentPassword } : {}),
      });
      if (response.ok) {
        const result = await response.json();
        setBackupCodes(Array.isArray(result.backupCodes) ? result.backupCodes : []);
        setStep('backup');
        toast({
          title: "MFA Enabled",
          description: "Multi-factor authentication has been enabled for your account.",
        });
      } else {
        await fail(response, "Invalid verification code");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to enable MFA. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setCopiedCodes(true);
    toast({
      title: "Copied",
      description: "Backup codes copied to clipboard",
    });
    setTimeout(() => setCopiedCodes(false), 2000);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    form.setValue('phoneNumber', formatted);
  };

  if (step === 'backup') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
            <Shield className="w-6 h-6 text-green-600" />
          </div>
          <CardTitle>Save Your Backup Codes</CardTitle>
          <CardDescription>
            Store these codes safely. You can use them to access your account if you lose your phone.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Save these codes in a secure location. Each code can only be used once.
            </AlertDescription>
          </Alert>

          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-2 mb-4">
              {backupCodes.map((code, index) => (
                <Badge key={index} variant="secondary" className="justify-center py-2">
                  {code}
                </Badge>
              ))}
            </div>
            
            <Button
              onClick={copyBackupCodes}
              variant="outline"
              className="w-full"
              disabled={copiedCodes}
            >
              {copiedCodes ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  Copy All Codes
                </>
              )}
            </Button>
          </div>

          <Button onClick={onComplete} className="w-full">
            I've Saved My Backup Codes
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (step === 'code') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <CardTitle>Enter the code</CardTitle>
          <CardDescription>
            We sent a 6-digit code to {form.getValues('phoneNumber')} on WhatsApp.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="text-center text-2xl tracking-widest"
          />
          <Button className="w-full" onClick={confirmCode} disabled={isLoading || code.length !== 6}>
            {isLoading ? "Verifying..." : "Verify and enable MFA"}
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => setStep('phone')} disabled={isLoading}>
            Use a different number
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="w-12 h-12 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
          <Shield className="w-6 h-6 text-blue-600" />
        </div>
        <CardTitle>Setup Multi-Factor Authentication</CardTitle>
        <CardDescription>
          Secure your {userRole} account with WhatsApp verification
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Alert className="mb-6">
          <MessageSquare className="h-4 w-4" />
          <AlertDescription>
            You'll receive verification codes via WhatsApp whenever you sign in from a new device.
          </AlertDescription>
        </Alert>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(enableMFA)} className="space-y-4">
            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>WhatsApp Phone Number</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Smartphone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        {...field}
                        onChange={handlePhoneChange}
                        placeholder="+1 (555) 123-4567"
                        className="pl-10"
                        type="tel"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {requirePassword && (
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="mfa-current-password">Current password</label>
                <Input
                  id="mfa-current-password"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>
            )}

            <div className="space-y-2">
              <h4 className="text-sm font-medium">What happens next:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• We'll send a 6-digit code to this WhatsApp number to confirm it</li>
                <li>• You'll receive backup codes to save securely</li>
                <li>• WhatsApp verification will be required for new devices</li>
                <li>• Your account will be more secure from unauthorized access</li>
              </ul>
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading || !form.watch('phoneNumber') || (!!requirePassword && !currentPassword)}
            >
              {isLoading ? "Sending code..." : "Send verification code"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}